import { useQuery } from '@tanstack/react-query';

/**
 * Runtime restrictions reported by the public `GET /health/status`.
 * `partnerBookingWrites` is 'off' while BRIDGE_DISABLE_WRITES gates the Guesty
 * reservation and webhook calls; `syncListingCap` is BRIDGE_SYNC_LIMIT, or
 * null when partner syncs are uncapped.
 */
export interface BridgeRestrictions {
  partnerBookingWrites: 'on' | 'off';
  syncListingCap: number | null;
}

interface HealthStatusResponse {
  restrictions: BridgeRestrictions;
  version: string;
}

export const healthKeys = {
  restrictions: () => ['health', 'restrictions'] as const,
};

async function fetchBridgeRestrictions(): Promise<BridgeRestrictions> {
  // Public, same-origin endpoint: no auth header, so not through the api client.
  const response = await fetch('/health/status');
  if (!response.ok) {
    throw new Error(`GET /health/status failed: HTTP ${String(response.status)}`);
  }
  const data = (await response.json()) as HealthStatusResponse;
  return data.restrictions;
}

export function useBridgeRestrictions() {
  return useQuery({
    queryKey: healthKeys.restrictions(),
    queryFn: fetchBridgeRestrictions,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}
